import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { API_BASE } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { TrendUp, Hash, Heart, MagnifyingGlass } from '@phosphor-icons/react';

function VideoGrid({ posts, onLike, userId }) {
  const navigate = useNavigate();
  if (!posts.length) {
    return <p className="text-center text-muted-foreground text-sm py-12 font-sans">No videos found</p>;
  }
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-px bg-border" data-testid="explore-video-grid">
      {posts.map(post => (
        <div key={post.post_id} className="aspect-[9/16] relative overflow-hidden group bg-black cursor-pointer" data-testid={`explore-post-${post.post_id}`}>
          {post.media_type === 'video' ? (
            <video src={`${API_BASE}/api/media/${post.media_path}`} className="w-full h-full object-cover" muted preload="metadata" />
          ) : (
            <img src={`${API_BASE}/api/media/${post.media_path}`} alt="" className="w-full h-full object-cover" loading="lazy" />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col justify-end p-3">
            <p className="text-white text-xs font-semibold truncate">@{post.user_name || 'golfer'}</p>
            {post.caption && <p className="text-white/80 text-xs truncate mt-0.5">{post.caption}</p>}
            <div className="flex items-center gap-1 mt-1">
              <Heart size={12} weight="fill" className="text-white" />
              <span className="text-white text-xs">{post.likes_count || 0}</span>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

export default function ExplorePage() {
  const [trending, setTrending] = useState([]);
  const [popularTags, setPopularTags] = useState([]);
  const [selectedTag, setSelectedTag] = useState(null);
  const [tagPosts, setTagPosts] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const navigate = useNavigate();

  const fetchExplore = useCallback(async () => {
    try {
      const [trendingRes, tagsRes] = await Promise.all([
        api.get('/posts/trending?limit=12'),
        api.get('/tags/popular?limit=20'),
      ]);
      setTrending(trendingRes.data.posts);
      setPopularTags(tagsRes.data);
    } catch (err) {
      console.error('Explore fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchExplore(); }, [fetchExplore]);

  const handleTagClick = async (tag) => {
    setSelectedTag(tag);
    try {
      const { data } = await api.get(`/posts/tag/${encodeURIComponent(tag)}?limit=20`);
      setTagPosts(data.posts);
    } catch {
      setTagPosts([]);
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!searchQuery.trim()) return;
    const tag = searchQuery.trim().replace(/^#/, '');
    handleTagClick(tag);
  };

  const clearTag = () => {
    setSelectedTag(null);
    setTagPosts([]);
    setSearchQuery('');
  };

  if (loading) {
    return (
      <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center bg-background">
        <div className="animate-pulse space-y-2 text-center">
          <div className="w-12 h-12 rounded-full bg-muted mx-auto" />
          <div className="w-24 h-3 bg-muted rounded mx-auto" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-background" data-testid="explore-page">
      <div className="max-w-2xl mx-auto px-4 py-8">
        {/* Header */}
        <h1 className="font-serif text-3xl sm:text-4xl text-foreground font-medium tracking-tight" data-testid="explore-title">
          Explore
        </h1>

        {/* Search */}
        <form onSubmit={handleSearch} className="mt-6 flex gap-2" data-testid="explore-search-form">
          <div className="flex-1 relative">
            <MagnifyingGlass size={18} className="absolute left-0 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search hashtags..."
              className="w-full bg-transparent border-0 border-b border-border focus:border-primary focus:outline-none focus:ring-0 pl-7 pr-2 py-2 text-sm font-sans placeholder:text-muted-foreground/50"
              data-testid="explore-search-input"
            />
          </div>
          <button
            type="submit"
            className="px-4 py-2 bg-primary text-primary-foreground rounded-sm text-xs font-semibold uppercase tracking-wide font-sans hover:bg-[#083313] transition-colors"
            data-testid="explore-search-btn"
          >
            Search
          </button>
        </form>

        {/* Popular Tags */}
        {popularTags.length > 0 && !selectedTag && (
          <div className="mt-8" data-testid="popular-tags-section">
            <h2 className="text-xs text-muted-foreground uppercase tracking-wide font-sans font-semibold flex items-center gap-2">
              <Hash size={14} weight="bold" /> Popular Hashtags
            </h2>
            <div className="flex flex-wrap gap-2 mt-3">
              {popularTags.map(({ tag, count }) => (
                <button
                  key={tag}
                  onClick={() => handleTagClick(tag)}
                  className="px-3 py-1.5 border border-border rounded-sm text-sm font-sans text-foreground hover:border-primary hover:text-primary transition-colors duration-300"
                  data-testid={`tag-${tag}`}
                >
                  #{tag} <span className="text-muted-foreground text-xs ml-1">{count}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Tag Results */}
        {selectedTag && (
          <div className="mt-6" data-testid="tag-results-section">
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-semibold font-sans text-foreground">
                #{selectedTag} <span className="text-muted-foreground font-normal">({tagPosts.length} videos)</span>
              </h2>
              <button
                onClick={clearTag}
                className="text-xs text-primary font-semibold font-sans uppercase tracking-wide hover:text-[#083313] transition-colors"
                data-testid="clear-tag-btn"
              >
                Clear
              </button>
            </div>
            <div className="mt-4">
              <VideoGrid posts={tagPosts} userId={user?.user_id} />
            </div>
          </div>
        )}

        {/* Trending */}
        {!selectedTag && (
          <div className="mt-8" data-testid="trending-section">
            <h2 className="text-xs text-muted-foreground uppercase tracking-wide font-sans font-semibold flex items-center gap-2">
              <TrendUp size={14} weight="bold" /> Trending Videos
            </h2>
            <div className="mt-4">
              {trending.length > 0 ? (
                <VideoGrid posts={trending} userId={user?.user_id} />
              ) : (
                <div className="text-center py-16">
                  <TrendUp size={36} weight="thin" className="mx-auto text-muted-foreground" />
                  <p className="text-muted-foreground text-sm mt-3 font-sans">No trending videos yet. Start posting!</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
